import { toNet, toNumber } from "../../shared/money";
import { itemTax } from "./data";
import { type BundleLine, isHourlyLabour, type MaterialRow, type PriceListData } from "./meta";

/*
 * What a bundle adds up to: each item at its own price and cost (ServiceM8 adds them to a job one
 * by one), before tax and with each item's tax, so items in different bases add up properly.
 */

export interface BundleTotals {
    priceNet: number;
    priceWithTax: number;
    costNet: number;
    /** Items in it that no longer exist (left out of the totals). */
    missing: number;
    /** Items in it with no cost (e.g. labour): its markup can't be worked out (it'd be far too high), though its profit can. */
    noCost: number;
}

/** Its items (of these lines) as they add up; `only`: just those it picks. */
export function bundleTotals(data: PriceListData, lines: readonly BundleLine[], only?: (m: MaterialRow) => boolean): BundleTotals {
    const byUuid = new Map(data.materials.map((m) => [m.uuid, m]));
    const totals: BundleTotals = { priceNet: 0, priceWithTax: 0, costNet: 0, missing: 0, noCost: 0 };
    for (const l of lines) {
        const m = byUuid.get(l.uuid);
        if (!m) {
            if (!only) totals.missing++;
            continue;
        }
        if (only && !only(m)) continue;
        const { tax } = itemTax(data, m);
        const priceNet = toNet(toNumber(m.price), m.price_includes_taxes, tax) * l.quantity;
        totals.priceNet += priceNet;
        totals.priceWithTax += priceNet * (1 + tax);
        totals.costNet += toNet(toNumber(m.cost), m.price_includes_taxes, tax) * l.quantity;
        if (toNumber(m.cost) <= 0) totals.noCost++;
    }
    return totals;
}

/** A bundle's parts and its labour (an hourly rate: its quantity is hours), each added up (bundleTotals), for its breakdown. */
export const bundleBreakdown = (data: PriceListData, lines: readonly BundleLine[]) => ({
    parts: bundleTotals(data, lines, (m) => !isLabourItem(data, m)),
    labour: bundleTotals(data, lines, (m) => isLabourItem(data, m)),
});

/** Labour, in a bundle or what an item's built from: an hourly rate (its quantity is hours), or a labour time (labourTime.ts). */
export const isLabourItem = (data: PriceListData, m: MaterialRow) => isHourlyLabour(m.name) || data.itemPricing[m.uuid]?.labour !== undefined;
